import { BaseApi } from "../base";
import { CompetencySetStatus } from "../models";
import { HttpMethodEnum } from "../types";
import { IJob, IJobInput, SeniorityLevel } from "../models/Job";
import { ReactAppSettings } from "../libs";
import { v4 } from "uuid";

export class JobsApi extends BaseApi {
    static async getJobs(): Promise<IJob[]> {
        return this.sendApiRequestWithResult<undefined, IJob[]>(HttpMethodEnum.GET, ReactAppSettings.appUrls.getJobs);
    }

    static async getSystemJobs(): Promise<IJob[]> {
        return this.sendApiRequestWithResult<undefined, IJob[]>(
            HttpMethodEnum.GET,
            ReactAppSettings.appUrls.getSystemJobs,
        );
    }

    static async getJob(id: string): Promise<IJob> {
        return this.sendApiRequestWithResult<undefined, IJob>(
            HttpMethodEnum.GET,
            ReactAppSettings.appUrls.getJob,
            undefined,
            id,
        );
    }

    static async createJob(input: IJobInput): Promise<IJob> {
        return this.sendApiRequestWithResult<IJobInput, IJob>(
            HttpMethodEnum.POST,
            ReactAppSettings.appUrls.createJob,
            input,
        );
    }

    static async updateJob(id: string, input: IJobInput): Promise<IJob> {
        return this.sendApiRequestWithResult<IJobInput, IJob>(
            HttpMethodEnum.PUT,
            ReactAppSettings.appUrls.updateJob,
            input,
            id,
        );
    }

    static async deleteJob(id: string): Promise<void> {
        return this.sendApiRequest(HttpMethodEnum.DELETE, ReactAppSettings.appUrls.deleteJob, undefined, id);
    }

    static async getJobCandidates(id: string): Promise<IJobEmployee[]> {
        return this.sendApiRequestWithResult<undefined, IJobEmployee[]>(
            HttpMethodEnum.GET,
            ReactAppSettings.appUrls.getJobCandidates,
            undefined,
            id,
        );
    }

    static async assignToEmployees(id: string, employeeIds: string[]): Promise<void> {
        const data = {
            candidateIds: employeeIds,
        };

        return this.sendApiRequestWithResult<typeof data, void>(
            HttpMethodEnum.POST,
            ReactAppSettings.appUrls.assignJobToEmployees,
            data,
            id,
        );
    }
}

export class JobsMockApi {
    private static readonly mockItems: IJob[] = [
        {
            id: "62581dbb-f0d2-4f10-95ce-641904550776",
            version: 1,
            clientId: ReactAppSettings?.viewBag?.baseInfo?.clientId,
            status: CompetencySetStatus.Published,
            name: "Software Developer",
            description: "Software Developer Description",
            localizedNames: [
                ["fr", "Développeur Logiciel"],
                ["en", "Software Developer"],
            ],
            localizedDescriptions: [
                ["fr", "Description d'un développeur logiciel"],
                ["en", "Software Developer Description"],
            ],
            departmentId: "tech",
            seniorityLevel: SeniorityLevel.Mid,
            competencyIds: ["StrategicVision"],
            createdFromId: v4(),
            candidatesCount: 0,
        },
        {
            id: "62581dbb-f0d2-4f10-95ce-641904550776",
            version: 1,
            clientId: ReactAppSettings?.viewBag?.baseInfo?.clientId,
            status: CompetencySetStatus.Published,
            name: "Analyst",
            description: "Covers analyst jobs in most sectors",
            localizedNames: [
                ["fr", "Analyste"],
                ["en", "Analyst"],
            ],
            localizedDescriptions: [
                ["fr", "Couvre les emplois d'analyste dans la plupart des secteurs"],
                ["en", "Covers analyst jobs in most sectors"],
            ],
            departmentId: "tech",
            seniorityLevel: SeniorityLevel.Senior,
            competencyIds: ["StrategicVision"],
            createdFromId: "62581dbb-f0d2-4f10-95ce-641904550776",
            candidatesCount: 0,
        },
    ];

    static async getJobs(): Promise<IJob[]> {
        return Promise.resolve(this.mockItems);
    }

    static async getJob(id: string): Promise<IJob | undefined> {
        return Promise.resolve(this.mockItems.find((x) => x.id === id));
    }

    static async createJob(input: IJobInput): Promise<IJob> {
        const { localizedNames, localizedDescriptions, ...restOfInput } = input;

        return Promise.resolve({
            ...this.mockItems[0],
            ...restOfInput,
            name: localizedNames.firstOrDefault()?.[1] ?? "",
            description: localizedDescriptions.firstOrDefault()?.[1] ?? "",
        });
    }

    static async updateJob(id: string, input: IJobInput): Promise<IJob> {
        const { localizedNames, localizedDescriptions, ...restOfInput } = input;

        return Promise.resolve({
            ...this.mockItems[0],
            ...restOfInput,
            name: localizedNames.firstOrDefault()?.[1] ?? "",
            description: localizedDescriptions.firstOrDefault()?.[1] ?? "",
        });
    }

    static async deleteJob(id: string): Promise<void> {
        return Promise.resolve();
    }
}

export interface IJobEmployee {
    candidateProId: string;
    displayName: string;
}
